import * as React from "react";
import { defaultFilter } from "cmdk";
import { Check, ChevronsUpDown, Plus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /**
   * Extra search-only terms for this option (cmdk `keywords`): matched by the
   * filter, never displayed. E.g. TimezoneSelect passes a zone's IANA aliases.
   */
  keywords?: string[];
  /**
   * Muted second line under the label, in the list rows only (never the
   * trigger). Display-only — put anything that should be searchable in
   * `keywords` too.
   */
  description?: string | null;
}

interface SearchableSelectProps {
  /**
   * Lands on the combobox trigger, so a sibling `<Label htmlFor>` (or
   * shadcn's `FormControl`, which injects one) actually associates with this
   * control — without it the picker has no programmatic name and clicking its
   * label does nothing.
   */
  id?: string;
  options: SearchableSelectOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  onCreateNew?: () => void;
  createNewText?: string;
  className?: string;
  disabled?: boolean;
  allowCustomValue?: boolean; // Allow entering custom text not in options
  /**
   * Server-side ("endpoint") search mode. When true the component does NOT
   * filter `options` itself (cmdk `shouldFilter={false}`) — the parent supplies
   * already-filtered options in response to `onSearchChange`. Use for
   * endpoint-backed pickers whose full option set is too large to load
   * client-side. Defaults to false (client-side filtering, unchanged).
   */
  manualFiltering?: boolean;
  /**
   * Custom client-side ranking for cmdk (ignored under `manualFiltering`).
   * Receives the item's text (the option label), the search string, and the
   * option's `keywords`; return 0 to hide, higher to rank. Use when cmdk's
   * default fuzzy subsequence matching is too loose for a large option set
   * (e.g. TimezoneSelect). Note it is also handed the synthetic
   * `__custom__<search>` item (allowCustomValue) and the "Create new" row —
   * a strict filter should return nonzero for those or it hides them.
   */
  filter?: (value: string, search: string, keywords?: string[]) => number;
  /** Called on every keystroke in the search box (raw text; debounce in the parent). */
  onSearchChange?: (search: string) => void;
  /** Show a loading row in the list (e.g. while an endpoint search is in flight). */
  loading?: boolean;
  /** Text for the loading row. */
  loadingText?: string;
}

export function SearchableSelect({
  id,
  options,
  value,
  onValueChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyText = "No options found.",
  onCreateNew,
  createNewText = "Create new",
  className,
  disabled = false,
  allowCustomValue = false,
  manualFiltering = false,
  filter,
  onSearchChange,
  loading = false,
  loadingText = "Loading…",
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [triggerEl, setTriggerEl] = React.useState<HTMLButtonElement | null>(null);

  // When the picker lives inside a dialog, collision-check against the dialog
  // instead of the viewport: Radix otherwise lets a tall option list (e.g. the
  // ~320-zone timezone picker) hang far past the modal's bottom edge. Outside
  // a dialog this is null and Radix falls back to the viewport as before.
  const dialogBoundary = triggerEl?.closest<HTMLElement>('[role="dialog"]') ?? undefined;

  const selectedOption = options.find((option) => option.value === value);

  // For allowCustomValue mode, show the value even if not in options
  const displayValue = selectedOption ? selectedOption.label : (allowCustomValue && value ? value : placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          // Explicit type: a native <button> defaults to type="submit", so
          // inside a <form> a bare trigger would SUBMIT the form on every
          // click (e.g. reopening the picker to change a selection) instead
          // of just toggling the popover.
          type="button"
          id={id}
          ref={setTriggerEl}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className={cn(
            "truncate",
            !selectedOption && allowCustomValue && value && "italic text-muted-foreground"
          )}>
            {displayValue}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // Tailwind 4 dropped the bare-var arbitrary-value shorthand: the old
        // `w-[--radix-popover-trigger-width]` silently generates NO css, so
        // the popover sized to its content and long option labels (e.g. the
        // timezone picker's) pushed it outside the parent dialog. Keep the
        // explicit var() form.
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0"
        align="start"
        collisionBoundary={dialogBoundary}
        collisionPadding={8}
      >
        {/* Items are keyed by `option.value` (unique) with the label as the
            first keyword, so two options with the same label no longer share
            cmdk's highlight identity (Enter always picked the first). The
            wrapper hands the label back as the scored value, so every filter
            — the default or a caller's — sees exactly (label, search,
            keywords) as before. Rows without keywords (Create new, the
            custom-value row) score on their own value unchanged. cmdk only
            re-reads a mounted item's keywords when its value changes; the
            popover unmounts on close, so a relabel shows on the next open. */}
        <Command
          shouldFilter={!manualFiltering}
          filter={(itemValue, search, keywords) => {
            const score = filter ?? defaultFilter;
            return keywords?.length
              ? score(keywords[0], search, keywords.slice(1))
              : score(itemValue, search, keywords);
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={(v) => {
              setSearchValue(v);
              onSearchChange?.(v);
            }}
          />
          {/* Cap the list to the space Radix reports within the collision
              boundary (minus the search input row) so the popover shrinks to
              fit the dialog instead of overflowing it; 300px stays the cmdk
              default upper bound. */}
          <CommandList className="max-h-[min(300px,calc(var(--radix-popover-content-available-height)-3.25rem))]">
            {loading && (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingText}
              </div>
            )}
            {!loading && (
            <CommandEmpty>
              <div className="px-4 py-6 text-center text-sm">
                <p className="text-muted-foreground leading-relaxed break-words">
                  {emptyText}
                </p>
                {allowCustomValue && searchValue && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      onValueChange(searchValue);
                      setOpen(false);
                      setSearchValue("");
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Use "{searchValue}"
                  </Button>
                )}
                {onCreateNew && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      onCreateNew();
                      setOpen(false);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {createNewText}
                  </Button>
                )}
              </div>
            </CommandEmpty>
            )}
            <CommandGroup>
              {allowCustomValue && searchValue && (
                <CommandItem
                  value={`__custom__${searchValue}`}
                  onSelect={() => {
                    onValueChange(searchValue);
                    setOpen(false);
                    setSearchValue("");
                  }}
                  className="text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Use "{searchValue}"
                </CommandItem>
              )}
              {onCreateNew && (
                <CommandItem
                  onSelect={() => {
                    onCreateNew();
                    setOpen(false);
                  }}
                  className="text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createNewText}
                </CommandItem>
              )}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label, ...(option.keywords ?? [])]}
                  onSelect={() => {
                    onValueChange(option.value === value ? "" : option.value);
                    setOpen(false);
                    setSearchValue("");
                    // Reset the parent's search term too (endpoint-search mode)
                    // so reopening shows the full list, not the last filtered subset.
                    onSearchChange?.("");
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.description ? (
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate">{option.label}</span>
                      <span className="truncate text-xs text-muted-foreground">{option.description}</span>
                    </span>
                  ) : (
                    option.label
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}